import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import RiskBadge from '../../components/ui/RiskBadge';
import MatchBadge from '../../components/investigation/MatchBadge';
import ResultKeywordTags from '../../components/investigation/ResultKeywordTags';
import SourceAttribution from '../../components/investigation/SourceAttribution';
import HighlightedPhoneSnippet from '../../components/investigation/HighlightedPhoneSnippet';
import { FeedInspectButton, FeedInspectUrlLink } from '../../components/FeedInspectActions';
import {
  FEED_CARD_SHELL,
  FEED_INSPECT_BTN,
  FEED_TITLE,
} from '../../components/investigation/feedCardStyles';
import {
  PROBE_STATE_STYLES,
  probeStateLabel,
} from '../../utils/nativeProbeStatus';
import {
  formatPhoneDisplayTag,
  isPhoneDebugNoiseTag,
  toTitleCaseLabel,
} from '../../utils/phoneUiFormat';
import {
  phoneE164FromResult,
  phoneLocationBadgeLabel,
  resolvePhoneLineKind,
} from '../../utils/phoneInputFormat';

const BUSINESS_OWNER_KEYS = ['service', 'calculatoare', 'shop', 'vanzari', 'vânzări'];

function WhatsAppGlyph({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
      />
    </svg>
  );
}

function ownerNameLooksCommercial(ownerName) {
  if (!ownerName || typeof ownerName !== 'string') return false;
  const n = ownerName.toLowerCase();
  return BUSINESS_OWNER_KEYS.some((k) => n.includes(k));
}

/** Mirror of backend ``_is_legitimate_owner_name`` (phone_lookup.py). */
function isLegitimateOwnerName(ownerName) {
  const name = String(ownerName || '').trim();
  if (name.length < 3) return false;
  const upper = name.toUpperCase();
  const blocked = ['NOT FOUND', 'NOT FOUND / UNCLAIMED', 'UNKNOWN', 'N/A', 'UNCLAIMED'];
  return !blocked.some((token) => upper.includes(token));
}

/** Digits for https://wa.me/{digits} (E.164 without plus). */
function phoneDigitsForWaMe(raw) {
  let digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10 && digits.startsWith('0')) {
    digits = `40${digits.slice(1)}`;
  } else if (digits.length === 9 && !digits.startsWith('0')) {
    digits = `40${digits}`;
  }
  return digits;
}

function breachListHasEmailLeak(breaches) {
  if (!Array.isArray(breaches)) return false;
  return breaches.some((b) =>
    (b?.data_classes || []).some((c) => String(c).toLowerCase().includes('email')),
  );
}

function formatBreachDate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const d = iso.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : iso;
}

function reasonCodeLabel(code) {
  const map = {
    HIBP_CLEAN: 'HIBP clean (no breach correlated)',
    BREACH_SIGNAL_PRESENT: 'Breach signal present in HIBP correlation',
    NO_PUBLIC_SIGNALS: 'No relevant public web signals detected',
    POSSIBLE_TELEMARKETING: 'Pattern matches possible telemarketing',
    SCAM_CLUSTER_SIGNAL: 'Cluster of risky reverse-lookup sources',
    DIRECTORY_ATTENTION: 'Single reverse-directory hit — attention, not confirmed scam',
    BUSINESS_WEB_FOOTPRINT: 'Business/public procurement footprint found',
    POSITIVE_WEB_FOOTPRINT: 'Positive web footprint found',
    OFFICIAL_ENTITY_MATCH: 'Official entity source matched',
  };
  return map[code] || String(code || '').replaceAll('_', ' ').toLowerCase();
}

/** Single phone intelligence row with optional public WhatsApp avatar modal. */
export default function PhoneResultCard({
  res,
  name,
  onPivotToPerson = null,
  hibpStatus = null,
  fullWidthMetrics = false,
}) {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [copiedE164, setCopiedE164] = useState(false);

  const isInfra = res.type === 'infrastructure' || res.source === 'phone_metadata';
  const isIdentity = res.type === 'phone_identity';
  const isWhatsAppIdentity = res.source === 'whatsapp';
  const profileImageUrl = res.details?.profile_image_url;
  const networkIdentityName = res.details?.identity_from_network === true;

  const waProbeStateRaw = String(res.details?.whatsapp_probe_state || '').toLowerCase();
  const waHasAccount = res.details?.whatsapp_has_account === true || waProbeStateRaw === 'active';
  const waProfilePicUrl = res.details?.whatsapp_profile_pic_url;
  const waProbeDetail = String(res.details?.whatsapp_probe_detail || '').replace(/_/g, ' ').trim();
  const waProbeUiState =
    waProbeStateRaw === 'ineligible'
      ? 'not_registered'
      : ['blocked', 'inconclusive', 'not_registered'].includes(waProbeStateRaw)
        ? waProbeStateRaw
        : null;
  const waBadgeLabel = waHasAccount
    ? waProfilePicUrl
      ? 'WhatsApp: Active (Public)'
      : 'WhatsApp: Active (Private)'
    : null;
  const avatarUrl = waProfilePicUrl || profileImageUrl || null;

  const tagsWithoutWhatsApp = (res.matched_tags || []).filter(
    (t) =>
      !String(t).startsWith('WhatsApp:') &&
      !String(t).startsWith('Owner:') &&
      !isPhoneDebugNoiseTag(t),
  );

  const commercialTag = 'Account Type: Commercial/Business';
  const displayTags = tagsWithoutWhatsApp.map(formatPhoneDisplayTag);
  if (
    ownerNameLooksCommercial(res.details?.owner_name) &&
    !displayTags.includes(commercialTag)
  ) {
    displayTags.push(commercialTag);
  }

  const carrierLabel = toTitleCaseLabel(res.details?.carrier);
  const showCircularAvatar =
    Boolean(avatarUrl) && (isWhatsAppIdentity || (isInfra && waHasAccount));
  const ownerName = String(res.details?.owner_name || '').trim();
  const showPivot =
    (isIdentity || isInfra) &&
    isLegitimateOwnerName(ownerName) &&
    typeof onPivotToPerson === 'function';
  const phoneRaw = res.target || res.details?.e164 || name;
  const lineKind = resolvePhoneLineKind(res);
  const isFixedLine = lineKind === 'FIXED_LINE';
  const isMobileLine = lineKind === 'MOBILE';
  const e164Value = phoneE164FromResult(res, phoneRaw);
  const locationBadge = phoneLocationBadgeLabel(res);
  const waDigits = phoneDigitsForWaMe(phoneRaw);
  const waHref =
    isMobileLine && waDigits && waHasAccount ? `https://wa.me/${waDigits}` : null;
  const smsHref = isMobileLine && e164Value ? `sms:${e164Value}` : null;
  const showQuickActions =
    (isIdentity || isInfra) && (waHref || smsHref || showPivot);
  const snippet = String(res.snippet || '').trim();

  const detailRiskRaw = res.details?.risk_score;
  const detailRiskNum =
    detailRiskRaw === undefined || detailRiskRaw === null ? NaN : Number(detailRiskRaw);
  const fallbackRisk = Number(res.risk_score ?? 0);
  const riskScoreForBorder = Number.isNaN(detailRiskNum) ? fallbackRisk : detailRiskNum;

  const metaScorePct = Math.max(
    0,
    Math.min(
      100,
      Math.round(
        Number.isFinite(Number(res.source_reliability))
          ? Number(res.source_reliability)
          : Math.max(0, 100 - riskScoreForBorder),
      ),
    ),
  );

  const lineBadgeLabel = isFixedLine
    ? locationBadge
      ? `Fixed Line / ${locationBadge.split(',')[0].trim()}`
      : 'Fixed Line'
    : isMobileLine
      ? 'Mobile'
      : lineKind !== 'UNKNOWN'
        ? lineKind.replace(/_/g, ' ')
        : '';

  const copyE164 = async () => {
    if (!e164Value) return;
    try {
      await navigator.clipboard.writeText(e164Value);
      setCopiedE164(true);
      window.setTimeout(() => setCopiedE164(false), 1500);
    } catch {
      setCopiedE164(false);
    }
  };

  const tagBlob = Array.isArray(res.matched_tags)
    ? res.matched_tags.map((t) => String(t).toLowerCase()).join(' ')
    : '';
  const hasExplicitSpoofing = tagBlob.includes('spoofing suspected');
  const hasExplicitFraud = tagBlob.includes('fraud reported');
  const hasTelemarketing = tagBlob.includes('telemarketing suspected');
  const hasDirectoryAttention = tagBlob.includes('directory attention');

  const isScamFlag = res.is_scam === true || res.details?.is_scam === true;
  const reputationRaw = String(res.reputation || res.details?.reputation || '').trim().toUpperCase();
  const isSuspiciousReputation = reputationRaw === 'SUSPICIOUS';
  /** Critical risk: score >= 70, explicit scam flag, or SUSPICIOUS reputation. */
  const criticalRisk =
    riskScoreForBorder >= 70 || isScamFlag || isSuspiciousReputation;

  const scamPulse = criticalRisk || (!Number.isNaN(detailRiskNum) && detailRiskNum >= 70);
  const scamAlert =
    criticalRisk || hasExplicitSpoofing || hasExplicitFraud || hasTelemarketing;
  const suspiciousAlert =
    !scamAlert &&
    (hasDirectoryAttention || (riskScoreForBorder >= 50 && riskScoreForBorder < 70));
  const verifiedBadge = !scamAlert && !suspiciousAlert;

  const riskBorderClass = scamAlert
    ? scamPulse
      ? 'border-2 border-red-600 animate-scam-border'
      : 'border-2 border-red-600'
    : suspiciousAlert
      ? 'border-2 border-amber-400'
      : 'border border-white/10';

  const showNoReportsBadge =
    res.details != null &&
    riskScoreForBorder === 0 &&
    !scamAlert &&
    (detailRiskRaw === 0 || detailRiskRaw === undefined || detailRiskRaw === null);

  const securityBreaches = Array.isArray(res.details?.security_breaches)
    ? res.details.security_breaches
    : [];
  const showBreachSection = securityBreaches.length > 0;
  const emailLeakBadge = breachListHasEmailLeak(securityBreaches);
  const auditReasonCodes = Array.isArray(res.audit?.reason_codes) ? res.audit.reason_codes : [];
  const hasAuditExplain = auditReasonCodes.length > 0;
  // Provider telemetry kept internal — never surface "Sources: …" debug chrome.

  const hibpBreachCount = Number(res.details?.hibp_breach_count ?? 0);
  const hibpBreached =
    isInfra &&
    (hibpStatus?.status === 'breached' || hibpBreachCount > 0 || securityBreaches.length > 0);
  const hibpClean = isInfra && res.details != null && !hibpBreached;
  /** Hide contradictory Clean badge when overall risk is critical. */
  const showHibpCleanBadge = hibpClean && !scamAlert && riskScoreForBorder < 70;

  useEffect(() => {
    if (!imageModalOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setImageModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [imageModalOpen]);

  const openImageModal = () => {
    if (showCircularAvatar) setImageModalOpen(true);
  };

  const modal =
    imageModalOpen &&
    avatarUrl &&
    createPortal(
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/80 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-label="Profile image"
        onClick={() => setImageModalOpen(false)}
      >
        <button
          type="button"
          className="absolute top-4 right-4 z-[201] w-10 h-10 rounded-full bg-white/10 text-white text-xl font-bold hover:bg-white/20"
          onClick={() => setImageModalOpen(false)}
          aria-label="Close"
        >
          ×
        </button>
        <img
          src={avatarUrl}
          alt="Profile"
          className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl ring-1 ring-white/20"
          onClick={(e) => e.stopPropagation()}
        />
      </div>,
      document.body,
    );

  return (
    <>
      <div
        className={`${FEED_CARD_SHELL} ${riskBorderClass} ${scamAlert ? 'hover:border-red-500/50' : suspiciousAlert ? 'hover:border-amber-500/50' : ''}`}
      >
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-4 sm:gap-5">
            <div
              role={showCircularAvatar ? 'button' : undefined}
              tabIndex={showCircularAvatar ? 0 : undefined}
              onClick={openImageModal}
              onKeyDown={(e) => {
                if (showCircularAvatar && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  openImageModal();
                }
              }}
              className={`flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden shadow-inner transition-all ${
                showCircularAvatar
                  ? 'cursor-pointer rounded-full bg-slate-800/80 ring-2 ring-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500'
                  : 'rounded-xl border border-slate-700/80 bg-slate-800/80 text-cyan-400'
              }`}
            >
              {showCircularAvatar ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="w-full h-full object-cover pointer-events-none"
                  loading="lazy"
                />
              ) : (
                <span className="text-2xl text-cyan-400">
                  {isInfra ? '📡' : isIdentity ? '👤' : '📱'}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-0.5 flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-center gap-1 text-[10px] font-black uppercase tracking-[0.15em] text-cyan-400">
                  {!isInfra ? (
                    <>
                      <SourceAttribution res={res} />
                      <RiskBadge />
                    </>
                  ) : (
                    <span className="text-slate-500">Phone Risk Summary</span>
                  )}
                  {scamAlert ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-md border border-red-500/50 bg-red-950/50 px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-red-400"
                      data-testid="phone-high-risk-banner"
                      role="alert"
                    >
                      <span aria-hidden>⚠️</span>
                      HIGH RISK: POTENTIAL SCAM / TELEMARKETING ALERT
                    </span>
                  ) : null}
                  {!scamAlert && suspiciousAlert ? (
                    <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-tight text-amber-900">
                      ATTENTION
                    </span>
                  ) : null}
                  {!scamAlert && verifiedBadge ? (
                    <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-tight text-emerald-800">
                      VERIFIED
                    </span>
                  ) : null}
                  {!scamAlert && showNoReportsBadge ? (
                    <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[9px] font-black uppercase tracking-tight text-emerald-800">
                      🛡️ No Reports Found
                    </span>
                  ) : null}
                </span>
                <span className="text-[9px] font-bold text-slate-300">
                  {new Date().toLocaleTimeString()}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className={`${FEED_TITLE} ${networkIdentityName ? 'opacity-70 italic' : ''}`}>
                  {name}
                </h3>
                {isInfra && e164Value ? (
                  <button
                    type="button"
                    onClick={copyE164}
                    data-testid="phone-copy-e164"
                    className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                      copiedE164
                        ? 'border-emerald-500/40 bg-emerald-950/40 text-emerald-400'
                        : 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                    title={e164Value}
                    aria-live="polite"
                  >
                    {copiedE164 ? (
                      <>
                        <span className="text-emerald-400" aria-hidden>
                          ✓
                        </span>
                        Copied!
                      </>
                    ) : (
                      <>
                        <span aria-hidden>📋</span>
                        Copy E.164
                      </>
                    )}
                  </button>
                ) : (
                  <MatchBadge
                    value={res.source_reliability ?? (scamAlert ? 25 : suspiciousAlert ? 55 : 72)}
                  />
                )}
              </div>
              {isInfra ? (
                <div
                  className={
                    fullWidthMetrics
                      ? 'mt-2 flex flex-wrap items-center justify-between gap-4'
                      : 'mt-2 space-y-1.5'
                  }
                  data-testid="phone-metadata-badges"
                  data-full-width={fullWidthMetrics ? 'true' : 'false'}
                >
                  {fullWidthMetrics ? (
                    <>
                      {carrierLabel ? (
                        <span className="inline-flex items-center rounded-md border border-slate-600/60 bg-slate-900/60 px-2 py-0.5 text-[10px] font-semibold text-slate-300">
                          Carrier: {carrierLabel}
                        </span>
                      ) : null}
                      {lineBadgeLabel ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-md border border-sky-400/30 bg-sky-950/40 px-2 py-0.5 text-[10px] font-semibold text-sky-100"
                          data-testid="phone-fixed-location-badge"
                        >
                          <span aria-hidden>📞</span>
                          {lineBadgeLabel}
                        </span>
                      ) : null}
                      {showHibpCleanBadge ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-md border border-emerald-400/35 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-200"
                          data-testid="phone-hibp-clean-badge"
                        >
                          🟢 Clean / {hibpBreachCount || 0} Breaches
                        </span>
                      ) : null}
                      {hibpBreached ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-md border border-red-400/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-200"
                          data-testid="phone-hibp-breach-badge"
                        >
                          🔴 Breaches · {hibpBreachCount || securityBreaches.length || '?'}
                        </span>
                      ) : null}
                      <span className="inline-flex items-center gap-1 rounded-md border border-slate-600/60 bg-slate-900/60 px-2 py-0.5 text-[10px] font-semibold text-slate-300">
                        🛡️ Score: {metaScorePct}%
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {lineBadgeLabel ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-md border border-sky-400/30 bg-sky-950/40 px-2 py-0.5 text-[10px] font-semibold text-sky-100"
                            data-testid="phone-fixed-location-badge"
                          >
                            <span aria-hidden>📞</span>
                            {lineBadgeLabel}
                          </span>
                        ) : null}
                        {carrierLabel ? (
                          <span className="inline-flex items-center rounded-md border border-slate-600/60 bg-slate-900/60 px-2 py-0.5 text-[10px] font-semibold text-slate-300">
                            Carrier: {carrierLabel}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {showHibpCleanBadge ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-md border border-emerald-400/35 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-200"
                            data-testid="phone-hibp-clean-badge"
                          >
                            🟢 Clean / {hibpBreachCount || 0} Breaches
                          </span>
                        ) : null}
                        {hibpBreached ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-md border border-red-400/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-200"
                            data-testid="phone-hibp-breach-badge"
                          >
                            🔴 Breaches · {hibpBreachCount || securityBreaches.length || '?'}
                          </span>
                        ) : null}
                        <span className="inline-flex items-center gap-1 rounded-md border border-slate-600/60 bg-slate-900/60 px-2 py-0.5 text-[10px] font-semibold text-slate-300">
                          🛡️ Score: {metaScorePct}%
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {!isInfra && snippet ? (
                    <HighlightedPhoneSnippet text={snippet} phoneRaw={phoneRaw} className="mt-1.5" />
                  ) : null}
                  <ResultKeywordTags res={res} />
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <FeedInspectUrlLink res={res} fallback="Telecommunication Record" />
                    {displayTags
                      .filter((tag) => !/provider\s*sensitive/i.test(tag))
                      .map((tag, tIdx) => (
                        <span
                          key={`${tag}-${tIdx}`}
                          className={
                            tag === commercialTag
                              ? 'rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-tighter text-amber-900'
                              : 'rounded border border-blue-100 bg-blue-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-tighter text-blue-600'
                          }
                        >
                          {tag}
                        </span>
                      ))}
                    {waBadgeLabel ? (
                      <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-tighter text-emerald-800">
                        {waBadgeLabel}
                      </span>
                    ) : null}
                    {waProbeUiState && !waHasAccount ? (
                      <span
                        className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2 py-0.5 opacity-90 ${
                          PROBE_STATE_STYLES[waProbeUiState] || PROBE_STATE_STYLES.inconclusive
                        }`}
                        title={
                          waProbeDetail
                            ? `WhatsApp — ${probeStateLabel(waProbeUiState)} · ${waProbeDetail}`
                            : `WhatsApp — ${probeStateLabel(waProbeUiState)}`
                        }
                        data-testid="phone-whatsapp-probe-badge"
                        data-probe-state={waProbeUiState}
                      >
                        <span className="truncate text-[10px] font-semibold leading-tight">
                          WhatsApp
                        </span>
                        <span className="shrink-0 rounded border border-current/20 px-1 py-px text-[8px] font-black uppercase tracking-wider opacity-90">
                          {probeStateLabel(waProbeUiState)}
                        </span>
                      </span>
                    ) : null}
                    {showCircularAvatar ? (
                      <button
                        type="button"
                        onClick={() => setImageModalOpen(true)}
                        className="ml-1 rounded-lg bg-slate-900 px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-white transition-colors hover:bg-blue-600"
                      >
                        View Full Image
                      </button>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
          {res.source !== 'phone_metadata' ? (
            <FeedInspectButton
              res={res}
              className={`${FEED_INSPECT_BTN} min-h-[44px] w-full sm:w-auto sm:min-h-0`}
            />
          ) : null}
        </div>
        {showQuickActions ? (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-white/5 pt-3">
            {waHref ? (
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-emerald-400/40 bg-emerald-950/40 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-emerald-100 hover:border-emerald-300/70 hover:bg-emerald-900/50"
                data-testid="phone-open-whatsapp"
              >
                <WhatsAppGlyph className="h-3.5 w-3.5 text-[#25D366]" />
                Open WhatsApp
              </a>
            ) : null}
            {smsHref ? (
              <a
                href={smsHref}
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-sky-400/40 bg-sky-950/40 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-sky-100 hover:border-sky-300/70 hover:bg-sky-900/50"
                data-testid="phone-open-sms"
              >
                SMS
              </a>
            ) : null}
            {showPivot ? (
              <button
                type="button"
                className="inline-flex min-h-[40px] w-full items-center justify-center rounded-lg border border-cyan-500/30 bg-cyan-500/10 py-2 text-center text-xs font-semibold text-cyan-300 transition hover:bg-cyan-500/20 sm:w-auto sm:px-3 sm:py-1.5"
                onClick={() =>
                  onPivotToPerson({
                    personName: ownerName,
                    companyName: '',
                    domain: '',
                  })
                }
              >
                👤 Pivot to Person Search
              </button>
            ) : null}
          </div>
        ) : null}
        {showBreachSection && (
          <div className="mt-4 pt-4 border-t border-red-100 w-full space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-[10px] font-black text-red-600 uppercase tracking-[0.12em]">
                🔴 SECURITY BREACHES DETECTED
              </h4>
              {emailLeakBadge && (
                <span className="px-2 py-0.5 rounded-md bg-rose-100 border border-rose-200 text-rose-900 text-[8px] font-black uppercase tracking-tight">
                  Email Found in Leak
                </span>
              )}
            </div>
            <ul className="space-y-2.5 list-none m-0 p-0">
              {securityBreaches.map((b, bi) => (
                <li
                  key={`${b?.name || b?.title || 'breach'}-${bi}`}
                  className="rounded-xl bg-red-50/60 border border-red-100 px-3 py-2.5"
                >
                  <a
                    href={b?.url || 'https://haveibeenpwned.com/'}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] font-black text-slate-900 uppercase tracking-tight hover:text-red-700"
                  >
                    {b?.title || 'Unknown breach'}
                  </a>
                  {formatBreachDate(b?.breach_date) && (
                    <p className="text-[9px] font-bold text-slate-500 mt-0.5">
                      Breach date: {formatBreachDate(b.breach_date)}
                    </p>
                  )}
                  {Array.isArray(b?.data_classes) && b.data_classes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {b.data_classes.map((dc, di) => (
                        <span
                          key={`${dc}-${di}`}
                          className="px-1.5 py-0.5 bg-white/80 border border-red-100 text-red-800 text-[7px] font-black rounded uppercase tracking-tighter"
                        >
                          {dc}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {hasAuditExplain && !isInfra && (
          <div className="mt-4 pt-4 border-t border-slate-100 w-full space-y-2">
            <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-[0.12em]">
              Why this verdict?
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {auditReasonCodes.map((code, i) => (
                <span
                  key={`${code}-${i}`}
                  className="px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-slate-700 text-[9px] font-bold"
                  title={String(code)}
                >
                  {reasonCodeLabel(code)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {modal}
    </>
  );
}
